import { Features } from '@backend/lib-feature-mgr';
import { useMemo } from 'react';

import { isIndexableName } from '~/business/IndexablePages/cacheIndexableNames';
import { findIndexableJobNames } from '~/business/IndexablePages/getIndexableJobAndSectorNames';
import type { SearchJobOfferOutput } from '~/business/JobOffer/searchJobOffers';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { Head, NOINDEX_FOLLOW } from '~/components/Head';
import { LatestJobOffersCard } from '~/components/LatestJobOffersCard/LatestJobOffersCard';
import { SEOHeader } from '~/components/SEOHeader';
import { SEOContainer, SEOText, SEOTitle } from '~/components/SEOText';
import { featureGuard } from '~/guards/featureGuard';
import { useCheckFeatureEnabled, useTranslation } from '~/hooks';
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs';
import { useGenerateSearchUrl } from '~/hooks/useGenerateSearchUrl';
import { useHubPageMeta } from '~/hooks/useHubPageMeta';
import type { JobListItem } from '~/lists/types';
import { getHubJobOffers } from '~/utils/hubJobOffers';
import type { HubPagination } from '~/utils/hubPage';
import { findJobBySlug } from '~/utils/list/findJobBySlug';
import { getJobTitles } from '~/utils/list/getJobTitles';
import { getParamValue } from '~/utils/params';

interface JobPageProps {
  jobListItem: JobListItem;
  isIndexable: boolean;
  latestJobOffers: SearchJobOfferOutput[];
  pagination: HubPagination;
}

const JobPage = ({ jobListItem, isIndexable, latestJobOffers, pagination }: JobPageProps) => {
  const t = useTranslation();
  const title = useHubPageMeta(t('job_page.meta_title', { job: jobListItem.name }), pagination);
  const description = useHubPageMeta(t('job_page.meta_description', { job: jobListItem.name }), pagination);
  const generateSearchUrl = useGenerateSearchUrl();
  const breadcrumbs = useBreadcrumbs({ page: 'jobs', subPages: [jobListItem.name] });
  const locationMandatoryFeature = useCheckFeatureEnabled(Features.JobBoardLocationMandatory);

  const seeAllOffersPath = useMemo(() => {
    if (locationMandatoryFeature) {
      return undefined;
    }
    return generateSearchUrl({ job: jobListItem.name });
  }, [locationMandatoryFeature, generateSearchUrl, jobListItem.name]);

  return (
    <>
      <Head title={title} description={description} robots={isIndexable ? undefined : NOINDEX_FOLLOW} />
      <SEOHeader title={t('job_page.title', { job: jobListItem.name })} />
      <Breadcrumbs items={breadcrumbs} />
      <SEOContainer>
        <SEOTitle content={t('job_page.heading', { job: jobListItem.name })} />
        <SEOText content={jobListItem.description} />
      </SEOContainer>
      <LatestJobOffersCard
        latestJobOffers={latestJobOffers}
        seeAllOffersPath={seeAllOffersPath}
        title={t('job_page.latest_job_offers', { job: jobListItem.name })}
        pagination={pagination}
      />
    </>
  );
};

export default JobPage;

export const getServerSideProps = featureGuard<JobPageProps>(Features.JobBoardSEO, async (businessUnit, context) => {
  const jobParam = getParamValue(context.params?.job)?.toLowerCase();

  if (!jobParam) {
    return { notFound: true };
  }

  const jobListItem = findJobBySlug(businessUnit, jobParam);

  if (!jobListItem) {
    return { notFound: true };
  }

  const [hubJobOffers, indexableJobNames] = await Promise.all([
    getHubJobOffers(businessUnit, context.query, { strictTitles: getJobTitles(jobListItem) }),
    findIndexableJobNames(businessUnit),
  ]);

  if (!hubJobOffers) {
    return { notFound: true };
  }

  return {
    props: {
      jobListItem,
      isIndexable: isIndexableName(indexableJobNames, jobListItem.name),
      ...hubJobOffers,
    },
  };
});
